import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

export interface AddressValidationResult {
  sellerId: string;
  sellerName: string;
  status: 'VALIDATED' | 'NEEDS_CONFIRMATION' | 'NEEDS_FIXING' | 'MISSING_UNIT' | 'UNVALIDATED' | 'ERROR';
  statusReason?: string;
  originalAddress: {
    line1?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  standardizedAddress?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zipCode5: string;
    zipCode4?: string;
    country: string;
    fullCityStateZip?: string;
  };
  validationDetails?: {
    addressComplete: boolean;
    hasUnconfirmedComponents: boolean;
    hasInferredComponents: boolean;
    hasReplacedComponents: boolean;
    hasSpellCorrectedComponents: boolean;
    dpvConfirmation?: string;
    uspsDeliverable: boolean;
    validationGranularity?: string;
    possibleNextAction?: string;
    missingComponentTypes?: string[];
    unresolvedTokens?: string[];
    suspiciousComponents?: string[];
  };
  errorMessage?: string;
  processedAt: any;
}

export interface ValidationSummary {
  total: number;
  validated: number;
  needsConfirmation: number;
  needsFixing: number;
  missingUnit: number;
  unvalidated: number;
  errors: number;
}

class AddressValidationService {
  private validateAddressesFunction = httpsCallable(functions, 'validateAddresses');
  private readonly BATCH_SIZE = 1000; // Firebase function limit
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 2000; // 2 seconds

  /**
   * Validate addresses with batch processing for large cohorts
   */
  async validateAddresses(sellerIds: string[]): Promise<{
    success: boolean;
    results: AddressValidationResult[];
    summary: ValidationSummary;
  }> {
    try {
      console.log(`🚀 Starting address validation for ${sellerIds.length} sellers`);
      
      // For cohorts within Firebase function limit, use single batch
      if (sellerIds.length <= this.BATCH_SIZE) {
        console.log(`📍 Single batch processing for ${sellerIds.length} sellers`);
        return await this.validateBatch(sellerIds);
      }
      
      // For large cohorts (>1000), use batch processing
      console.log(`📦 Large cohort detected (${sellerIds.length} sellers), using batch processing`);
      return await this.validateInBatches(sellerIds);
      
    } catch (error) {
      console.error('Failed to validate addresses:', error);
      throw new Error('Failed to validate addresses');
    }
  }

  /**
   * Validate a single batch of seller IDs
   */
  private async validateBatch(sellerIds: string[], retryCount = 0): Promise<{
    success: boolean;
    results: AddressValidationResult[];
    summary: ValidationSummary;
  }> {
    try {
      console.log(`📍 Validating batch of ${sellerIds.length} sellers (attempt ${retryCount + 1})`);
      
      const result = await this.validateAddressesFunction({
        sellerIds,
        timestamp: Date.now()
      });
      
      const data = result.data as any;
      console.log(`✅ Batch validation completed: ${data.results?.length || 0} results`);
      
      // DEBUG: Log the actual structure we received
      if (data && data.results && data.results.length > 0) {
        console.log('🔍 DEBUG: First 3 results from Firebase function:', JSON.stringify(data.results.slice(0, 3), null, 2));
      }
      
      return data;
    } catch (error) {
      console.warn(`⚠️ Batch validation failed (attempt ${retryCount + 1}):`, error);
      
      if (retryCount < this.MAX_RETRIES) {
        // Wait before retry with exponential backoff
        const waitTime = this.RETRY_DELAY * Math.pow(2, retryCount);
        console.log(`⏳ Waiting ${waitTime}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        
        return await this.validateBatch(sellerIds, retryCount + 1);
      }
      
      throw error;
    }
  }

  /**
   * Validate addresses in batches for large cohorts
   */
  private async validateInBatches(sellerIds: string[]): Promise<{
    success: boolean;
    results: AddressValidationResult[];
    summary: ValidationSummary;
  }> {
    console.log(`📦 Processing ${sellerIds.length} sellers in batches of ${this.BATCH_SIZE}`);
    
    const allResults: AddressValidationResult[] = [];
    const totalBatches = Math.ceil(sellerIds.length / this.BATCH_SIZE);
    let successfulBatches = 0;
    let failedBatches = 0;
    
    for (let i = 0; i < sellerIds.length; i += this.BATCH_SIZE) {
      const batchNumber = Math.floor(i / this.BATCH_SIZE) + 1;
      const batch = sellerIds.slice(i, i + this.BATCH_SIZE);
      
      try {
        console.log(`🔄 Processing batch ${batchNumber}/${totalBatches} (${batch.length} sellers)`);
        
        const batchResult = await this.validateBatch(batch);
        
        if (batchResult.success && batchResult.results) {
          allResults.push(...batchResult.results);
          successfulBatches++;
          console.log(`✅ Batch ${batchNumber}/${totalBatches} completed: ${batchResult.results.length} results`);
        } else {
          failedBatches++;
          console.error(`❌ Batch ${batchNumber}/${totalBatches} failed: No results returned`);
          
          // Create error results for failed batch
          const errorResults = batch.map(sellerId => ({
            sellerId,
            sellerName: 'Unknown',
            status: 'ERROR' as const,
            statusReason: 'Batch processing failed',
            originalAddress: {},
            errorMessage: 'Failed to process this batch',
            processedAt: new Date()
          }));
          allResults.push(...errorResults);
        }
        
        // Small delay between batches to avoid overwhelming the server
        if (i + this.BATCH_SIZE < sellerIds.length) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
        }
        
      } catch (error) {
        failedBatches++;
        console.error(`❌ Batch ${batchNumber}/${totalBatches} failed:`, error);
        
        // Create error results for failed batch
        const errorResults = batch.map(sellerId => ({
          sellerId,
          sellerName: 'Unknown',
          status: 'ERROR' as const,
          statusReason: 'Batch processing failed',
          originalAddress: {},
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          processedAt: new Date()
        }));
        allResults.push(...errorResults);
      }
    }
    
    console.log(`🎯 Batch processing completed: ${successfulBatches}/${totalBatches} batches successful, ${failedBatches} failed`);
    console.log(`📊 Total results: ${allResults.length}/${sellerIds.length} sellers processed`);
    
    // Calculate summary
    const summary = this.calculateSummary(allResults);
    
    return {
      success: successfulBatches > 0, // Success if at least one batch succeeded
      results: allResults,
      summary
    };
  }

  /**
   * Calculate validation summary from results
   */
  private calculateSummary(results: AddressValidationResult[]): ValidationSummary {
    const summary: ValidationSummary = {
      total: results.length,
      validated: 0,
      needsConfirmation: 0,
      needsFixing: 0,
      missingUnit: 0,
      unvalidated: 0,
      errors: 0
    };

    results.forEach(result => {
      switch (result.status) {
        case 'VALIDATED':
          summary.validated++;
          break;
        case 'NEEDS_CONFIRMATION':
          summary.needsConfirmation++;
          break;
        case 'NEEDS_FIXING':
          summary.needsFixing++;
          break;
        case 'MISSING_UNIT':
          summary.missingUnit++;
          break;
        case 'UNVALIDATED':
          summary.unvalidated++;
          break;
        case 'ERROR':
          summary.errors++;
          break;
      }
    });

    return summary;
  }

  /**
   * Format address for display
   */
  formatAddress(address: AddressValidationResult['originalAddress'] | AddressValidationResult['standardizedAddress']): string {
    if (!address) return 'No address';
    
    const parts = [];
    if (address.line1) parts.push(address.line1);
    if ('line2' in address && address.line2) parts.push(address.line2);
    
    const cityStateZip = [];
    if (address.city) cityStateZip.push(address.city);
    if (address.state) cityStateZip.push(address.state);
    
    if ('zipCode5' in address) {
      // Standardized address format
      let zip = address.zipCode5;
      if (address.zipCode4) zip += `-${address.zipCode4}`;
      if (zip) cityStateZip.push(zip);
    } else if (address.zip) {
      // Original address format
      cityStateZip.push(address.zip);
    }
    
    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '));
    }
    
    return parts.join('\n');
  }

  /**
   * Get status color for UI display
   */
  getStatusColor(status: AddressValidationResult['status']): string {
    switch (status) {
      case 'VALIDATED':
        return 'text-green-600 dark:text-green-400';
      case 'UNVALIDATED':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'ERROR':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  }

  /**
   * Get status badge variant for UI display
   */
  getStatusBadgeVariant(status: AddressValidationResult['status']): 'default' | 'secondary' | 'destructive' {
    switch (status) {
      case 'VALIDATED':
        return 'default'; // Green
      case 'UNVALIDATED':
        return 'secondary'; // Yellow/Orange
      case 'ERROR':
        return 'destructive'; // Red
      default:
        return 'secondary';
    }
  }

  /**
   * Get human-readable status text
   */
  getStatusText(status: AddressValidationResult['status']): string {
    switch (status) {
      case 'VALIDATED':
        return 'Validated';
      case 'UNVALIDATED':
        return 'Unvalidated';
      case 'ERROR':
        return 'Error';
      default:
        return 'Unknown';
    }
  }

  /**
   * Check if address has standardized version
   */
  hasStandardizedAddress(result: AddressValidationResult): boolean {
    return !!(result.standardizedAddress && result.standardizedAddress.line1);
  }

  /**
   * Get validation quality score (0-100)
   */
  getValidationScore(result: AddressValidationResult): number {
    if (result.status === 'ERROR') return 0;
    if (result.status === 'VALIDATED') return 100;
    
    // For UNVALIDATED, calculate partial score based on validation details
    const details = result.validationDetails;
    if (!details) return 25;
    
    let score = 25; // Base score for having some data
    
    if (details.addressComplete) score += 25;
    if (details.uspsDeliverable) score += 25;
    if (!details.hasUnconfirmedComponents) score += 15;
    if (!details.hasInferredComponents) score += 10;
    
    return Math.min(score, 95); // Cap at 95 for unvalidated
  }

  /**
   * Get validation details summary
   */
  getValidationSummary(result: AddressValidationResult): string {
    if (result.status === 'ERROR') {
      return result.errorMessage || 'Validation failed';
    }
    
    if (result.status === 'VALIDATED') {
      return 'Address validated by USPS';
    }
    
    // For UNVALIDATED, provide detailed explanation
    const details = result.validationDetails;
    if (!details) return 'No validation details available';
    
    const issues = [];
    if (!details.addressComplete) issues.push('incomplete address');
    if (!details.uspsDeliverable) issues.push('not USPS deliverable');
    if (details.hasUnconfirmedComponents) issues.push('unconfirmed components');
    if (details.hasInferredComponents) issues.push('inferred components');
    
    if (issues.length === 0) {
      return 'Address partially validated';
    }
    
    return `Issues: ${issues.join(', ')}`;
  }
}

export const addressValidationService = new AddressValidationService();